import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { toast } from 'sonner'

import {
    getUserProfile,
    updateUserProfile,
    uploadProfilePicture,
    removeProfilePicture,
} from '@/api/actions/users/usersActions'
import { Queries } from '@/api/queries.enum'
import { Mutations } from '@/api/mutations.enum'
import type {
    IProfileResponse,
    IUpdateProfilePayload,
    IUpdateProfileResponse,
    IProfilePictureUploadResponse,
    IProfilePictureRemoveResponse,
    IProfileApiError,
    IProfilePictureError,
    ICurrentUserResponse,
} from '@/api/actions/users/usersActions.types'

interface IUseUserProfileOptions {
    userUuid: string
    enabled?: boolean
}

export function useUserProfile(options: IUseUserProfileOptions) {
    const { userUuid, enabled = true } = options

    // Validate UUID format
    const isValidUuid = (uuid: string): boolean => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
        return uuidRegex.test(uuid)
    }

    return useQuery<IProfileResponse, AxiosError<IProfileApiError>>({
        queryKey: [Queries.getUserProfile, userUuid],
        queryFn: () => {
            if (!userUuid || !isValidUuid(userUuid)) {
                throw new Error('Invalid user UUID provided')
            }
            return getUserProfile(userUuid)
        },
        enabled: enabled && !!userUuid && isValidUuid(userUuid),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 10, // 10 minutes
        retry: (failureCount, error) => {
            // Don't retry on 400 errors (validation errors)
            if (error.response?.status === 400) {
                return false
            }
            // Retry up to 2 times for other errors
            return failureCount < 2
        },
    })
}

interface IUseUpdateProfileOptions {
    onSuccess?: (data: IUpdateProfileResponse) => void
    onError?: (error: AxiosError<IProfileApiError>) => void
}

export function useUpdateProfile(options?: IUseUpdateProfileOptions) {
    const queryClient = useQueryClient()

    return useMutation<
        IUpdateProfileResponse,
        AxiosError<IProfileApiError>,
        { userUuid: string; payload: IUpdateProfilePayload }
    >({
        mutationKey: [Mutations.updateUserProfile],
        mutationFn: ({ userUuid, payload }) => updateUserProfile(userUuid, payload),
        onSuccess: (data, variables) => {
            // Invalidate and refetch user profile
            queryClient.invalidateQueries({ queryKey: [Queries.getUserProfile, variables.userUuid] })

            // Invalidate current user if updating own profile
            queryClient.invalidateQueries({ queryKey: [Queries.getCurrentUser] })

            toast.success('Profile updated successfully')
            options?.onSuccess?.(data)
        },
        onError: (error) => {
            const errorData = error.response?.data
            const errorMessage = errorData?.error || 'Failed to update profile. Please try again.'

            toast.error(errorMessage)
            options?.onError?.(error)
        },
    })
}

interface IUseUploadProfilePictureOptions {
    onSuccess?: (data: IProfilePictureUploadResponse) => void
    onError?: (error: AxiosError<IProfilePictureError>) => void
}

export function useUploadProfilePicture(options?: IUseUploadProfilePictureOptions) {
    const queryClient = useQueryClient()

    return useMutation<
        IProfilePictureUploadResponse,
        AxiosError<IProfilePictureError>,
        { userUuid: string; file: File }
    >({
        mutationKey: [Mutations.uploadProfilePicture],
        mutationFn: ({ userUuid, file }) => {
            // Validate inputs before making API call
            if (!userUuid) {
                throw new Error('User UUID is required')
            }
            if (!file) {
                throw new Error('File is required')
            }
            if (file.size > 5 * 1024 * 1024) {
                throw new Error('File size must be less than 5MB')
            }
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
            if (!validTypes.includes(file.type)) {
                throw new Error('File must be a JPG, PNG, GIF, or WebP image')
            }
            return uploadProfilePicture(userUuid, file)
        },
        onSuccess: (data, variables) => {
            // Update current user cache immediately with new profile picture URL
            queryClient.setQueryData([Queries.getCurrentUser], (oldData: ICurrentUserResponse | undefined) => {
                if (oldData) {
                    return {
                        ...oldData,
                        profile_picture_url: data.profile_picture_url,
                        profile_picture_uploaded_at: data.uploaded_at,
                    }
                }
                return oldData
            })

            // Update user profile cache immediately with new profile picture URL
            queryClient.setQueryData(
                [Queries.getUserProfile, variables.userUuid],
                (oldData: IProfileResponse | undefined) => {
                    if (oldData) {
                        return {
                            ...oldData,
                            profile_picture_url: data.profile_picture_url,
                            profile_picture_uploaded_at: data.uploaded_at,
                        }
                    }
                    return oldData
                },
            )

            // Invalidate and refetch user profile to ensure consistency
            queryClient.invalidateQueries({ queryKey: [Queries.getUserProfile, variables.userUuid] })

            // Invalidate current user if updating own profile
            queryClient.invalidateQueries({ queryKey: [Queries.getCurrentUser] })

            toast.success('Profile picture uploaded successfully')
            options?.onSuccess?.(data)
        },
        onError: (error) => {
            const errorData = error.response?.data
            let errorMessage = 'Failed to upload profile picture. Please try again.'

            if (errorData?.code === 'validation_error') {
                errorMessage = errorData.error || errorMessage
            } else if (errorData?.code === 'processing_error') {
                errorMessage = 'Failed to process image. Please check the file format and try again.'
            } else if (errorData?.code === 'storage_error') {
                errorMessage = 'Upload failed due to server error. Please try again later.'
            }

            toast.error(errorMessage)
            options?.onError?.(error)
        },
    })
}

interface IUseRemoveProfilePictureOptions {
    onSuccess?: (data: IProfilePictureRemoveResponse) => void
    onError?: (error: AxiosError<IProfilePictureError>) => void
}

export function useRemoveProfilePicture(options?: IUseRemoveProfilePictureOptions) {
    const queryClient = useQueryClient()

    return useMutation<IProfilePictureRemoveResponse, AxiosError<IProfilePictureError>, { userUuid: string }>({
        mutationKey: [Mutations.removeProfilePicture],
        mutationFn: ({ userUuid }) => removeProfilePicture(userUuid),
        onSuccess: (data, variables) => {
            // Update current user cache immediately with removed profile picture
            queryClient.setQueryData([Queries.getCurrentUser], (oldData: ICurrentUserResponse | undefined) => {
                if (oldData) {
                    return {
                        ...oldData,
                        profile_picture_url: null,
                        profile_picture_uploaded_at: null,
                    }
                }
                return oldData
            })

            // Update user profile cache immediately with removed profile picture
            queryClient.setQueryData(
                [Queries.getUserProfile, variables.userUuid],
                (oldData: IProfileResponse | undefined) => {
                    if (oldData) {
                        return {
                            ...oldData,
                            profile_picture_url: null,
                            profile_picture_uploaded_at: null,
                        }
                    }
                    return oldData
                },
            )

            // Invalidate and refetch user profile to ensure consistency
            queryClient.invalidateQueries({ queryKey: [Queries.getUserProfile, variables.userUuid] })

            // Invalidate current user if updating own profile
            queryClient.invalidateQueries({ queryKey: [Queries.getCurrentUser] })

            toast.success('Profile picture removed successfully')
            options?.onSuccess?.(data)
        },
        onError: (error) => {
            const errorData = error.response?.data
            const errorMessage = errorData?.error || 'Failed to remove profile picture. Please try again.'

            toast.error(errorMessage)
            options?.onError?.(error)
        },
    })
}
